import React from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Loader,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import GoalItem from "./GoalItem";
import { useAutoAnimate } from "@formkit/auto-animate/react";

const CategoryCard = ({
  category,
  goalCount,
  onAddGoal,
  onEditCategory,
  onDeleteCategory,
  onEditGoal,
  onDeleteGoal,
  isDeleting = false,
  deletingGoalId = null,
}) => {
  const [parent] = useAutoAnimate();
  const theme = useMantineTheme();
  const safeCategory = category || { id: "", name: "", color: "gray", goals: [] };
  const goals = Array.isArray(safeCategory.goals) ? safeCategory.goals : [];
  // The card receives the search-filtered goals, so the badge counts the whole
  // category instead of whatever survived the filter.
  const totalGoals = goalCount ?? goals.length;
  const barColor = theme.colors[safeCategory.color]?.[6] || theme.colors.gray[4];
  const name = safeCategory.name || "Untitled";

  return (
    <Paper radius="lg" withBorder p="md">
      <Flex justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
        {/* minWidth: 0 is what stops a long category name from pushing the
            action buttons onto their own line — without it the flex item
            refuses to shrink below its text width. */}
        <Flex
          gap="sm"
          align="flex-start"
          wrap="nowrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Box
            sx={{
              width: 8,
              height: 24,
              marginTop: 4,
              background: barColor,
              borderRadius: 4,
              flexShrink: 0,
            }}
          />
          <Stack spacing={6} sx={{ minWidth: 0 }}>
            <Tooltip
              label={name}
              multiline
              width={280}
              withinPortal
              disabled={name.length <= 40}
            >
              <Title
                order={4}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  lineHeight: 1.3,
                }}
              >
                {name}
              </Title>
            </Tooltip>
            <Badge color="gray" variant="light" sx={{ alignSelf: "flex-start" }}>
              {totalGoals} goals
            </Badge>
          </Stack>
        </Flex>

        <Group spacing="xs" noWrap sx={{ flexShrink: 0 }}>
          <Button
            size="xs"
            leftIcon={<IconPlus size={14} />}
            variant="light"
            onClick={onAddGoal}
          >
            Add
          </Button>
          <ActionIcon
            variant="subtle"
            onClick={onEditCategory}
            aria-label="Edit category"
          >
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={onDeleteCategory}
            disabled={isDeleting}
            aria-label="Delete category"
          >
            {isDeleting ? <Loader size="xs" /> : <IconTrash size={16} />}
          </ActionIcon>
        </Group>
      </Flex>

      {safeCategory.description && (
        <Text size="sm" c="dimmed" mt="sm" lineClamp={2}>
          {safeCategory.description}
        </Text>
      )}

      <ScrollArea w={"100%"} h={"15rem"} mt="md">
        <Stack spacing="xs" ref={parent}>
          {goals.map((g) => (
            <GoalItem
              key={g.id}
              goal={g}
              onEdit={() => onEditGoal(g)}
              onDelete={() => onDeleteGoal(g)}
              isDeleting={deletingGoalId === g.id}
            />
          ))}
          {goals.length === 0 && (
            <Text c="dimmed" size="sm" ml={8}>
              No goals yet. Click Add to create one.
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  );
};

export default CategoryCard;
